import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { computeTotals, money } from "@/lib/totals";
import { printReceipt, printZReport, printInternalSlip } from "@/lib/receipt";
import { ItemsGrid } from "@/components/pos/ItemsGrid";
import { PaymentGrid } from "@/components/pos/PaymentGrid";
import { ActionPanel } from "@/components/pos/ActionPanel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Package, Receipt } from "lucide-react";

const uid = () =>
  (crypto.randomUUID && crypto.randomUUID()) ||
  Math.random().toString(36).slice(2);

export default function POS() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [cart, setCart] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [payments, setPayments] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [loyaltyRedeemed, setLoyaltyRedeemed] = useState(0);
  const [openingAmount, setOpeningAmount] = useState(0);
  const [barcode, setBarcode] = useState("");
  const [lastBill, setLastBill] = useState(null);
  const [clock, setClock] = useState(new Date());

  const [dlg, setDlg] = useState(null);
  const [v1, setV1] = useState("");
  const [v2, setV2] = useState("");
  const [dlgLine, setDlgLine] = useState(null);
  const [custForm, setCustForm] = useState({ name: "", phone: "", email: "" });
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [heldBills, setHeldBills] = useState([]);
  const [payMethod, setPayMethod] = useState(null);
  const [edcStatus, setEdcStatus] = useState("idle");
  const [edcResult, setEdcResult] = useState(null);
  const barcodeRef = useRef(null);
  const [quickAdd, setQuickAdd] = useState({ barcode: "", name: "", price: "", tax: "5", save: false });
  const [billsList, setBillsList] = useState([]);
  const [billQuery, setBillQuery] = useState("");
  const [zreport, setZreport] = useState(null);
  const [edcMode, setEdcMode] = useState("simulated");
  const [returnBill, setReturnBill] = useState(null);
  const [returnQtys, setReturnQtys] = useState({});
  const [settings, setSettings] = useState({});
  const [journalDate, setJournalDate] = useState(() => new Date().toISOString().slice(0, 10));

  const totals = useMemo(() => computeTotals(cart, discount), [cart, discount]);
  const paid = useMemo(
    () => payments.reduce((s, p) => s + p.amount, 0),
    [payments]
  );
  const remaining = Math.max(0, +(totals.balance - paid).toFixed(2));

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    api
      .get("/payments/edc/config")
      .then((r) => setEdcMode(r.data.mode))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (!dlg) setTimeout(() => barcodeRef.current?.focus(), 50);
  }, [dlg]);

  const closeDlg = () => {
    setDlg(null);
    setV1("");
    setV2("");
    setDlgLine(null);
    setEdcStatus("idle");
    setEdcResult(null);
    setPayMethod(null);
  };

  // ---------------- cart ops ----------------
  const addProduct = (p) => {
    setCart((prev) => {
      const idx = prev.findIndex(
        (l) => l.barcode === p.barcode && l.price === p.price
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        setSelectedId(next[idx].line_id);
        return next;
      }
      const line = {
        line_id: uid(),
        product_id: p.id || null,
        barcode: p.barcode,
        item_alias: p.item_alias,
        price: p.price,
        qty: 1,
        tax_rate: p.tax_rate ?? 5,
      };
      setSelectedId(line.line_id);
      return [...prev, line];
    });
  };

  const scan = async (code) => {
    const c = code.trim();
    if (!c) return;
    try {
      const { data } = await api.get(`/products/lookup/${encodeURIComponent(c)}`);
      addProduct(data);
      setBarcode("");
      barcodeRef.current?.focus();
    } catch {
      setQuickAdd({ barcode: c, name: "", price: "", tax: "5", save: false });
      setDlg("quick_add");
    }
  };

  const clearBillState = () => {
    setCart([]);
    setPayments([]);
    setDiscount(0);
    setLoyaltyRedeemed(0);
    setCustomer(null);
    setSelectedId(null);
  };

  // ---------------- payments ----------------
  const onPay = (p) => {
    if (cart.length === 0) return toast.error("Cart is empty");
    if (remaining <= 0) return toast.info("Bill already fully paid");
    setPayMethod(p);
    if (p.type === "cash") {
      setV1(remaining.toFixed(2));
      setDlg("cash");
    } else if (p.type === "edc") {
      setV1(remaining.toFixed(2));
      setEdcStatus("idle");
      setEdcResult(null);
      setDlg("edc");
    } else if (p.type === "credit") {
      addPayment({ method: "PAY CREDIT", amount: remaining, reference: "" });
    } else if (p.type === "coupon") {
      setV1(remaining.toFixed(2));
      setDlg("coupon");
    }
  };

  const addPayment = (payment) => {
    const next = [...payments, payment];
    setPayments(next);
    const totalPaid = next.reduce((s, x) => s + x.amount, 0);
    if (totalPaid + 0.009 >= totals.balance) {
      finalizeBill(next);
    } else {
      toast.success(
        `${payment.method}: ${money(payment.amount)} · Remaining ${money(
          totals.balance - totalPaid
        )}`
      );
    }
  };

  const finalizeBill = async (allPayments) => {
    try {
      const body = {
        lines: cart.map((l) => ({
          product_id: l.product_id,
          barcode: l.barcode,
          item_alias: l.item_alias,
          price: l.price,
          qty: l.qty,
          tax_rate: l.tax_rate,
        })),
        payments: allPayments,
        discount,
        customer_id: customer?.id || null,
        loyalty_redeemed: loyaltyRedeemed,
      };
      const { data } = await api.post("/bills", body);
      setLastBill({ ...data, _customer: customer });
      printReceipt(data, customer, settings);
      printInternalSlip(data);
      toast.success(`Bill ${data.bill_number} completed`);
      clearBillState();
      setDlg("receipt");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete bill");
    }
  };

  const runEdc = async () => {
    const amount = parseFloat(v1);
    if (!amount || amount <= 0) return toast.error("Enter a valid amount");
    setEdcStatus("processing");
    try {
      const { data } = await api.post("/payments/edc/sale", {
        amount,
        method: payMethod.method,
      });
      const mid = data.merchant_transaction_id;
      let state = data.status;
      let detail = null;
      const deadline = Date.now() + 180000;
      while (state === "SUBMITTED" || state === "PENDING") {
        await new Promise((r) => setTimeout(r, edcMode === "real" ? 5000 : 700));
        if (Date.now() > deadline) break;
        const s = await api.get(`/payments/edc/${mid}/status`);
        state = s.data.status;
        detail = s.data;
      }
      if (!detail) {
        const s = await api.get(`/payments/edc/${mid}/status`);
        detail = s.data;
        state = detail.status;
      }
      if (state === "APPROVED") {
        setEdcResult(detail);
        setEdcStatus("approved");
        setTimeout(() => {
          addPayment({
            method: payMethod.method,
            amount: detail.amount || amount,
            reference: detail.rrn ? `RRN ${detail.rrn}` : "",
          });
          closeDlg();
        }, 1200);
      } else {
        setEdcStatus("declined");
      }
    } catch {
      setEdcStatus("declined");
    }
  };

  // ---------------- actions ----------------
  const onAction = (key) => {
    const sel = cart.find((l) => l.line_id === selectedId);
    switch (key) {
      case "void_item":
        if (!sel) return toast.error("Select an item first");
        setCart((prev) => prev.filter((l) => l.line_id !== selectedId));
        setSelectedId(null);
        toast.success("Item voided");
        break;
      case "quantity":
        if (!sel) return toast.error("Select an item first");
        setDlgLine(sel);
        setV1(String(sel.qty));
        setV2(String(sel.price));
        setDlg("quantity");
        break;
      case "clear_bill":
        if (cart.length === 0) return;
        setDlg("clear_confirm");
        break;
      case "void_payment":
        if (payments.length === 0) return toast.info("No payments to void");
        setPayments((prev) => prev.slice(0, -1));
        toast.success("Last payment voided");
        break;
      case "recall":
        api.get("/bills/held").then((r) => {
          setHeldBills(r.data);
          setDlg("recall");
        });
        break;
      case "opening_amount":
        setV1(String(openingAmount || ""));
        setDlg("opening");
        break;
      case "redeem_loyalty":
        if (!customer) return toast.error("Select a customer first");
        if (!customer.loyalty_points)
          return toast.info("No loyalty points available");
        setV1("");
        setDlg("redeem");
        break;
      case "issue_loyalty":
        if (!customer) return toast.error("Select a customer first");
        toast.success(
          `Will earn ${Math.floor(totals.balance / 100)} loyalty points on checkout`
        );
        break;
      case "pay_credit_memo":
        setV1("");
        setDlg("credit_memo");
        break;
      case "emp_disc":
        setV1("");
        setDlg("emp_disc");
        break;
      case "tender_declaration":
        setV1("");
        setDlg("tender");
        break;
      case "logoff":
        openZReport();
        break;
      default:
        break;
    }
  };

  const holdBill = async () => {
    if (cart.length === 0) return toast.error("Nothing to hold");
    try {
      await api.post("/bills/hold", {
        lines: cart.map((l) => ({
          product_id: l.product_id,
          barcode: l.barcode,
          item_alias: l.item_alias,
          price: l.price,
          qty: l.qty,
          tax_rate: l.tax_rate,
        })),
        discount,
        customer_id: customer?.id || null,
      });
      toast.success("Bill held");
      clearBillState();
    } catch {
      toast.error("Could not hold bill");
    }
  };

  const recallBill = async (h) => {
    setCart(h.lines.map((l) => ({ ...l, line_id: uid() })));
    setDiscount(h.discount || 0);
    await api.delete(`/bills/held/${h.id}`);
    closeDlg();
    toast.success("Bill recalled");
  };

  const openZReport = async () => {
    const { data } = await api.get("/reports/zreport");
    setZreport(data);
    setDlg("zreport");
  };

  const openBills = async () => {
    setBillQuery("");
    const { data } = await api.get("/bills/search");
    setBillsList(data);
    setDlg("bills");
  };

  const downloadJournal = async () => {
    const d = journalDate || new Date().toISOString().slice(0, 10);
    try {
      const res = await api.get(`/reports/journal?date=${d}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `journal_${d}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Day journal downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Download failed");
    }
  };

  const openReturn = (b) => {
    const rq = b.returned_qty || [];
    const init = {};
    b.lines.forEach((l, i) => {
      init[i] = String(Math.max(0, l.qty - (rq[i] || 0)));
    });
    setReturnBill(b);
    setReturnQtys(init);
    setDlg("return");
  };

  const submitReturn = async (all) => {
    if (!returnBill) return;
    let items = null;
    if (!all) {
      items = Object.entries(returnQtys)
        .map(([i, q]) => ({ index: Number(i), qty: parseFloat(q) || 0 }))
        .filter((x) => x.qty > 0);
      if (items.length === 0) return toast.error("Enter a quantity to return");
    }
    try {
      const { data } = await api.post(
        `/bills/${returnBill.id}/return`,
        items ? { items } : {}
      );
      toast.success(`Refund ${data.bill_number} created (₹${money(-data.balance)})`);
      const { data: list } = await api.get(
        `/bills/search?q=${encodeURIComponent(billQuery)}`
      );
      setBillsList(list);
      setDlg("bills");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Return failed");
    }
  };

  const cashSales = payments
    .filter((p) => p.method === "CASH")
    .reduce((s, p) => s + p.amount, 0);

  // ---------------- render ----------------
  const change =
    dlg === "cash"
      ? Math.max(0, (parseFloat(v1) || 0) - remaining)
      : 0;

  return (
    <div className="pos-shell">
      <div className="pos-titlebar">
        <span>RETAIL LTD — Point of Sale</span>
        <div className="right">
          <button className="title-btn" onClick={openBills} data-testid="nav-bills">
            <Receipt size={13} style={{ display: "inline", marginRight: 4 }} />
            Bills
          </button>
          <button className="title-btn" onClick={() => setDlg("journal")} data-testid="nav-journal">
            Day Journal
          </button>
          {user?.role === "admin" && (
            <>
              <button
                className="title-btn"
                onClick={() => navigate("/products")}
                data-testid="nav-products"
              >
                <Package size={13} style={{ display: "inline", marginRight: 4 }} />
                Products
              </button>
              <button className="title-btn" onClick={() => navigate("/settings")} data-testid="nav-settings">
                Settings
              </button>
              <button className="title-btn" onClick={() => navigate("/users")} data-testid="nav-users">
                Users
              </button>
            </>
          )}
          <button className="title-btn" onClick={holdBill} data-testid="hold-bill">
            Hold Bill
          </button>
          <span>
            {user?.name} ({user?.role})
          </span>
          <button className="title-btn" onClick={openZReport} data-testid="logoff-top">
            Log Off
          </button>
        </div>
      </div>

      <div className="pos-main">
        <div className="pos-left">
          <ItemsGrid
            lines={cart}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onEditPrice={(l) => {
              setDlgLine(l);
              setV1(String(l.price));
              setDlg("price");
            }}
          />

          <div className="arrows">
            <button
              className="arrow-btn"
              data-testid="scroll-up"
              onClick={() => {
                const i = cart.findIndex((l) => l.line_id === selectedId);
                if (i > 0) setSelectedId(cart[i - 1].line_id);
              }}
            >
              ▲
            </button>
            <button
              className="arrow-btn"
              data-testid="scroll-down"
              onClick={() => {
                const i = cart.findIndex((l) => l.line_id === selectedId);
                if (i < cart.length - 1) setSelectedId(cart[i + 1].line_id);
              }}
            >
              ▼
            </button>
            <div style={{ flex: 1 }} />
            <span style={{ fontSize: 11, color: "#6b5a6d", alignSelf: "center" }}>
              Double-click a price to edit it manually
            </span>
          </div>

          <div className="pos-bottom">
            <div>
              <PaymentGrid onPay={onPay} />
              <div className="cust-row">
                <div
                  className="cust-link"
                  onClick={() => {
                    setCustForm({ name: "", phone: "", email: "" });
                    setDlg("new_customer");
                  }}
                  data-testid="new-customer"
                >
                  <Plus size={16} /> New customer
                </div>
                <div
                  className="cust-link"
                  onClick={() => {
                    setCustQuery("");
                    setCustResults([]);
                    setDlg("search_customer");
                  }}
                  data-testid="search-customer"
                >
                  <Search size={16} /> Search customers
                </div>
                {customer && (
                  <span style={{ fontSize: 12, color: "#a11b4a" }} data-testid="active-customer">
                    {customer.name} · {customer.loyalty_points} pts
                  </span>
                )}
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div className="barcode-box">
                <div className="barcode-row">
                  <Input
                    ref={barcodeRef}
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && scan(barcode)}
                    placeholder="Bar code or item id"
                    className="pos-input"
                    data-testid="barcode-input"
                    autoFocus
                  />
                  <button
                    className="arrow-btn"
                    style={{ width: 42 }}
                    onClick={() => scan(barcode)}
                    data-testid="barcode-add"
                  >
                    ⤵
                  </button>
                </div>
              </div>
              <div className="totals">
                <div className="row">
                  <span>Total discount</span>
                  <span className="v" data-testid="t-discount">
                    {money(totals.discount)}
                  </span>
                </div>
                <div className="row">
                  <span>Number of items</span>
                  <span className="v" data-testid="t-count">
                    {totals.count}
                  </span>
                </div>
                <div className="row">
                  <span>Tax</span>
                  <span className="v" data-testid="t-tax">
                    {money(totals.tax)}
                  </span>
                </div>
                <div className="row">
                  <span>Subtotal</span>
                  <span className="v" data-testid="t-subtotal">
                    {money(totals.net)}
                  </span>
                </div>
                <div className="row balance">
                  <span>Balance</span>
                  <span data-testid="t-balance">{money(totals.balance)}</span>
                </div>
                {payments.length > 0 && (
                  <div className="row" style={{ color: "#a11b4a" }}>
                    <span>Paid / Remaining</span>
                    <span className="v" data-testid="t-remaining">
                      {money(paid)} / {money(remaining)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <ActionPanel onAction={onAction} />
      </div>

      <div className="pos-statusbar">
        <span>
          Cash register: HK2404 &nbsp; Shift: HK2404i2 &nbsp; {user?.name}
        </span>
        <span>
          Connected to store database &nbsp;|&nbsp; Opening: {money(openingAmount)}{" "}
          &nbsp;|&nbsp; {clock.toLocaleString("en-IN")}
        </span>
      </div>

      {/* ---------------- dialogs ---------------- */}
      <Dialog open={dlg === "cash"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Cash Payment</DialogTitle>
          </DialogHeader>
          <div className="row" style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Amount due</span>
            <b>₹ {money(remaining)}</b>
          </div>
          <label className="login-label">Tender received</label>
          <Input
            type="number"
            value={v1}
            onChange={(e) => setV1(e.target.value)}
            data-testid="cash-tender"
            autoFocus
          />
          <div className="big-amount" data-testid="cash-change">
            Change ₹ {money(change)}
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                const amt = Math.min(parseFloat(v1) || 0, remaining);
                if (amt <= 0) return toast.error("Enter tender amount");
                addPayment({ method: "CASH", amount: amt, reference: "" });
                closeDlg();
              }}
              data-testid="cash-confirm"
            >
              Confirm Cash
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "edc"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">
              {payMethod?.label} — Paytm EDC {edcMode === "real" ? "(Live Device)" : "(Simulated)"}
            </DialogTitle>
          </DialogHeader>
          {edcStatus === "idle" && (
            <>
              <label className="login-label">Amount to charge</label>
              <Input
                type="number"
                value={v1}
                onChange={(e) => setV1(e.target.value)}
                data-testid="edc-amount"
                autoFocus
              />
              <div className="edc-screen">
                PAYTM EDC — {edcMode === "real" ? "LIVE DEVICE" : "SIMULATED"}<br />
                {edcMode === "real"
                  ? "Press SEND to push the amount to your paired terminal…"
                  : "Press SEND to run the demo approval…"}
              </div>
              <DialogFooter>
                <Button onClick={runEdc} data-testid="edc-send">
                  Send to Machine
                </Button>
              </DialogFooter>
            </>
          )}
          {edcStatus === "processing" && (
            <div className="edc-screen">
              <div className="pulse">PROCESSING ON PAYTM DEVICE…</div>
              <div style={{ marginTop: 10 }}>
                Amount: ₹ {money(parseFloat(v1) || 0)}
              </div>
              <div style={{ marginTop: 6 }}>
                Tap / Swipe / Insert card<span className="edc-dot">_</span>
              </div>
            </div>
          )}
          {edcStatus === "approved" && edcResult && (
            <div className="edc-screen" data-testid="edc-approved">
              *** TRANSACTION APPROVED ***<br />
              AMT : ₹ {money(edcResult.amount)}<br />
              CARD: {edcResult.masked_card || "—"}<br />
              TYPE: {edcResult.card_type || "—"}<br />
              RRN : {edcResult.rrn || "—"}<br />
              AUTH: {edcResult.auth_code || "—"}<br />
              TID : {edcResult.terminal_id || "—"}
            </div>
          )}
          {edcStatus === "declined" && (
            <div className="edc-screen" style={{ color: "#ff6c6c" }}>
              *** TRANSACTION DECLINED ***
              <DialogFooter>
                <Button onClick={() => setEdcStatus("idle")}>Retry</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "coupon"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Coupon / Gift Card</DialogTitle>
          </DialogHeader>
          <label className="login-label">Coupon amount</label>
          <Input
            type="number"
            value={v1}
            onChange={(e) => setV1(e.target.value)}
            data-testid="coupon-amount"
            autoFocus
          />
          <DialogFooter>
            <Button
              onClick={() => {
                const amt = Math.min(parseFloat(v1) || 0, remaining);
                if (amt <= 0) return toast.error("Enter amount");
                addPayment({ method: "COUPON-GIFT", amount: amt, reference: "" });
                closeDlg();
              }}
              data-testid="coupon-confirm"
            >
              Apply Coupon
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "quantity"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Edit Quantity & Price</DialogTitle>
          </DialogHeader>
          <div style={{ fontSize: 13, marginBottom: 6 }}>{dlgLine?.item_alias}</div>
          <label className="login-label">Quantity</label>
          <Input
            type="number"
            value={v1}
            onChange={(e) => setV1(e.target.value)}
            data-testid="qty-value"
            autoFocus
          />
          <label className="login-label" style={{ marginTop: 10 }}>
            Unit price (manual edit)
          </label>
          <Input
            type="number"
            value={v2}
            onChange={(e) => setV2(e.target.value)}
            data-testid="qty-price"
          />
          <DialogFooter>
            <Button
              onClick={() => {
                const q = parseFloat(v1);
                const pr = parseFloat(v2);
                if (!q || q <= 0) return toast.error("Invalid quantity");
                setCart((prev) =>
                  prev.map((l) =>
                    l.line_id === dlgLine.line_id
                      ? { ...l, qty: q, price: pr >= 0 ? pr : l.price }
                      : l
                  )
                );
                closeDlg();
                toast.success("Line updated");
              }}
              data-testid="qty-confirm"
            >
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "price"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Manual Price Edit</DialogTitle>
          </DialogHeader>
          <div style={{ fontSize: 13, marginBottom: 6 }}>{dlgLine?.item_alias}</div>
          <label className="login-label">New unit price</label>
          <Input
            type="number"
            value={v1}
            onChange={(e) => setV1(e.target.value)}
            data-testid="price-value"
            autoFocus
          />
          <DialogFooter>
            <Button
              onClick={() => {
                const pr = parseFloat(v1);
                if (!(pr >= 0)) return toast.error("Invalid price");
                setCart((prev) =>
                  prev.map((l) =>
                    l.line_id === dlgLine.line_id ? { ...l, price: pr } : l
                  )
                );
                closeDlg();
                toast.success("Price updated");
              }}
              data-testid="price-confirm"
            >
              Save Price
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "opening"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Opening Cash Amount</DialogTitle>
          </DialogHeader>
          <Input
            type="number"
            value={v1}
            onChange={(e) => setV1(e.target.value)}
            data-testid="opening-value"
            autoFocus
          />
          <DialogFooter>
            <Button
              onClick={() => {
                setOpeningAmount(parseFloat(v1) || 0);
                closeDlg();
                toast.success("Opening amount set");
              }}
              data-testid="opening-confirm"
            >
              Set
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "redeem"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Redeem Loyalty Points</DialogTitle>
          </DialogHeader>
          <div style={{ fontSize: 13 }}>
            Available: {customer?.loyalty_points} pts (1 pt = ₹1)
          </div>
          <Input
            type="number"
            value={v1}
            onChange={(e) => setV1(e.target.value)}
            data-testid="redeem-value"
            autoFocus
          />
          <DialogFooter>
            <Button
              onClick={() => {
                const pts = Math.min(
                  parseInt(v1 || "0", 10),
                  customer.loyalty_points,
                  Math.floor(totals.balance)
                );
                if (pts <= 0) return toast.error("Invalid points");
                setLoyaltyRedeemed(pts);
                setDiscount((d) => d + pts);
                closeDlg();
                toast.success(`Redeemed ${pts} points (₹${pts} off)`);
              }}
              data-testid="redeem-confirm"
            >
              Redeem
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "credit_memo"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Pay Credit Memo (Old)</DialogTitle>
          </DialogHeader>
          <label className="login-label">Credit memo value to adjust</label>
          <Input
            type="number"
            value={v1}
            onChange={(e) => setV1(e.target.value)}
            data-testid="memo-value"
            autoFocus
          />
          <DialogFooter>
            <Button
              onClick={() => {
                const amt = Math.min(parseFloat(v1) || 0, totals.balance);
                if (amt <= 0) return toast.error("Invalid amount");
                setDiscount((d) => d + amt);
                closeDlg();
                toast.success(`Credit memo ₹${money(amt)} applied`);
              }}
              data-testid="memo-confirm"
            >
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "emp_disc"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Employee Discount (V2)</DialogTitle>
          </DialogHeader>
          <label className="login-label">Discount percent on bill</label>
          <Input
            type="number"
            value={v1}
            onChange={(e) => setV1(e.target.value)}
            data-testid="emp-disc-value"
            autoFocus
          />
          <DialogFooter>
            <Button
              onClick={() => {
                const pct = parseFloat(v1) || 0;
                if (pct <= 0 || pct > 100) return toast.error("Enter 1-100");
                const amt = +((totals.gross * pct) / 100).toFixed(2);
                setDiscount(amt);
                closeDlg();
                toast.success(`${pct}% employee discount applied`);
              }}
              data-testid="emp-disc-confirm"
            >
              Apply Discount
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "tender"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Tender Declaration</DialogTitle>
          </DialogHeader>
          <div className="row" style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Expected cash (opening + cash sales)</span>
            <b>₹ {money(openingAmount + cashSales)}</b>
          </div>
          <label className="login-label">Counted cash</label>
          <Input
            type="number"
            value={v1}
            onChange={(e) => setV1(e.target.value)}
            data-testid="tender-value"
            autoFocus
          />
          <DialogFooter>
            <Button
              onClick={() => {
                const counted = parseFloat(v1) || 0;
                const diff = counted - (openingAmount + cashSales);
                closeDlg();
                toast[diff === 0 ? "success" : "warning"](
                  `Declared ₹${money(counted)} · Difference ₹${money(diff)}`
                );
              }}
              data-testid="tender-confirm"
            >
              Declare
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "clear_confirm"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Clear Bill?</DialogTitle>
          </DialogHeader>
          <div>This will remove all items and payments from the current bill.</div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDlg}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                clearBillState();
                closeDlg();
                toast.success("Bill cleared");
              }}
              data-testid="clear-confirm"
            >
              Clear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "new_customer"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">New Customer</DialogTitle>
          </DialogHeader>
          <label className="login-label">Name</label>
          <Input
            value={custForm.name}
            onChange={(e) => setCustForm({ ...custForm, name: e.target.value })}
            data-testid="cust-name"
            autoFocus
          />
          <label className="login-label" style={{ marginTop: 8 }}>
            Phone
          </label>
          <Input
            value={custForm.phone}
            onChange={(e) => setCustForm({ ...custForm, phone: e.target.value })}
            data-testid="cust-phone"
          />
          <label className="login-label" style={{ marginTop: 8 }}>
            Email
          </label>
          <Input
            value={custForm.email}
            onChange={(e) => setCustForm({ ...custForm, email: e.target.value })}
            data-testid="cust-email"
          />
          <DialogFooter>
            <Button
              onClick={async () => {
                if (!custForm.name.trim()) return toast.error("Name required");
                const { data } = await api.post("/customers", custForm);
                setCustomer(data);
                closeDlg();
                toast.success("Customer added");
              }}
              data-testid="cust-save"
            >
              Save Customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "search_customer"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Search Customers</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              value={custQuery}
              onChange={(e) => setCustQuery(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  const { data } = await api.get(`/customers?q=${custQuery}`);
                  setCustResults(data);
                }
              }}
              placeholder="Name or phone"
              data-testid="cust-search-input"
              autoFocus
            />
            <Button
              onClick={async () => {
                const { data } = await api.get(`/customers?q=${custQuery}`);
                setCustResults(data);
              }}
              data-testid="cust-search-go"
            >
              Search
            </Button>
          </div>
          <div style={{ maxHeight: 240, overflowY: "auto", marginTop: 8 }}>
            {custResults.map((c) => (
              <div
                key={c.id}
                style={{
                  padding: "8px 6px",
                  borderBottom: "1px solid #eee",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                }}
                onClick={() => {
                  setCustomer(c);
                  closeDlg();
                  toast.success(`Customer: ${c.name}`);
                }}
                data-testid={`cust-result-${c.id}`}
              >
                <span>
                  {c.name} · {c.phone || "-"}
                </span>
                <span>{c.loyalty_points} pts</span>
              </div>
            ))}
            {custResults.length === 0 && (
              <div style={{ color: "#999", padding: 10 }}>No results</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "recall"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Recall Held Bills</DialogTitle>
          </DialogHeader>
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {heldBills.map((h) => (
              <div
                key={h.id}
                style={{
                  padding: "10px 6px",
                  borderBottom: "1px solid #eee",
                  cursor: "pointer",
                }}
                onClick={() => recallBill(h)}
                data-testid={`held-${h.id}`}
              >
                {h.lines.length} items ·{" "}
                {new Date(h.created_at).toLocaleString("en-IN")}
              </div>
            ))}
            {heldBills.length === 0 && (
              <div style={{ color: "#999", padding: 10 }}>No held bills</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "receipt"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">
              Bill {lastBill?.bill_number} — Completed
            </DialogTitle>
          </DialogHeader>
          {lastBill && (
            <div style={{ fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Items</span>
                <span>{lastBill.item_count}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Tax</span>
                <span>₹ {money(lastBill.tax_total)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Discount</span>
                <span>₹ {money(lastBill.discount)}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: "bold",
                  fontSize: 18,
                  marginTop: 6,
                }}
              >
                <span>Total</span>
                <span>₹ {money(lastBill.balance)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Change</span>
                <span>₹ {money(lastBill.change)}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={closeDlg}>
              New Bill
            </Button>
            <Button
              onClick={() => {
                printReceipt(lastBill, lastBill?._customer, settings);
                printInternalSlip(lastBill);
              }}
              data-testid="print-receipt"
            >
              Print Receipts (x2)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "quick_add"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Add Item Manually</DialogTitle>
          </DialogHeader>
          <label className="login-label">Barcode / Item ID</label>
          <Input
            value={quickAdd.barcode}
            onChange={(e) => setQuickAdd({ ...quickAdd, barcode: e.target.value })}
            data-testid="qa-barcode"
          />
          <label className="login-label" style={{ marginTop: 8 }}>
            Item name
          </label>
          <Input
            value={quickAdd.name}
            onChange={(e) => setQuickAdd({ ...quickAdd, name: e.target.value })}
            data-testid="qa-name"
            autoFocus
          />
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1 }}>
              <label className="login-label" style={{ marginTop: 8 }}>
                Price (incl. tax)
              </label>
              <Input
                type="number"
                value={quickAdd.price}
                onChange={(e) => setQuickAdd({ ...quickAdd, price: e.target.value })}
                data-testid="qa-price"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label className="login-label" style={{ marginTop: 8 }}>
                Tax %
              </label>
              <Input
                type="number"
                value={quickAdd.tax}
                onChange={(e) => setQuickAdd({ ...quickAdd, tax: e.target.value })}
                data-testid="qa-tax"
              />
            </div>
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 10,
              fontSize: 13,
            }}
          >
            <input
              type="checkbox"
              checked={quickAdd.save}
              onChange={(e) => setQuickAdd({ ...quickAdd, save: e.target.checked })}
              data-testid="qa-save-catalog"
            />
            Also save to product catalog
          </label>
          <DialogFooter>
            <Button
              onClick={async () => {
                const price = parseFloat(quickAdd.price);
                if (!quickAdd.name.trim() || !(price >= 0))
                  return toast.error("Enter item name and price");
                const tax = parseFloat(quickAdd.tax) || 0;
                const bc = quickAdd.barcode.trim() || "MAN" + Date.now();
                addProduct({
                  id: null,
                  barcode: bc,
                  item_alias: quickAdd.name.trim(),
                  price,
                  tax_rate: tax,
                });
                if (quickAdd.save) {
                  try {
                    await api.post("/products", {
                      barcode: bc,
                      item_alias: quickAdd.name.trim(),
                      price,
                      tax_rate: tax,
                      stock: 0,
                    });
                  } catch (err) {
                    if (err.response?.status !== 400)
                      toast.error("Could not save to catalog");
                  }
                }
                setBarcode("");
                closeDlg();
                toast.success("Item added to bill");
              }}
              data-testid="qa-confirm"
            >
              Add to Bill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "bills"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Past Bills — Reprint & Returns</DialogTitle>
          </DialogHeader>
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              value={billQuery}
              onChange={(e) => setBillQuery(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter") {
                  const { data } = await api.get(`/bills/search?q=${encodeURIComponent(billQuery)}`);
                  setBillsList(data);
                }
              }}
              placeholder="Bill number or customer"
              data-testid="bill-search-input"
            />
            <Button
              onClick={async () => {
                const { data } = await api.get(`/bills/search?q=${encodeURIComponent(billQuery)}`);
                setBillsList(data);
              }}
              data-testid="bill-search-go"
            >
              Search
            </Button>
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto", marginTop: 8 }}>
            {billsList.map((b) => (
              <div
                key={b.id}
                style={{
                  padding: "8px 6px",
                  borderBottom: "1px solid #eee",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
                data-testid={`bill-${b.id}`}
              >
                <div style={{ fontSize: 12 }}>
                  <b>{b.bill_number}</b>
                  {b.is_return ? " (REFUND)" : ""}
                  {b.returned ? " · returned" : b.partially_returned ? " · partial" : ""}
                  <br />
                  {new Date(b.created_at).toLocaleString("en-IN")} · ₹{money(b.balance)} ·{" "}
                  {b.item_count} items
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <Button
                    variant="outline"
                    onClick={() =>
                      printReceipt(b, b.customer_name ? { name: b.customer_name } : null, settings)
                    }
                    data-testid={`reprint-${b.id}`}
                  >
                    Reprint
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => printInternalSlip(b)}
                    data-testid={`internal-${b.id}`}
                  >
                    Internal
                  </Button>
                  {!b.is_return && !b.returned && (
                    <Button onClick={() => openReturn(b)} data-testid={`return-${b.id}`}>
                      Return
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {billsList.length === 0 && (
              <div style={{ color: "#999", padding: 10 }}>No bills found</div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "return"} onOpenChange={(o) => !o && setDlg("bills")}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">
              Return Items — {returnBill?.bill_number}
            </DialogTitle>
          </DialogHeader>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {returnBill?.lines.map((l, i) => {
              const already = (returnBill.returned_qty || [])[i] || 0;
              const remaining = l.qty - already;
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    padding: "6px 0",
                    borderBottom: "1px solid #eee",
                  }}
                  data-testid={`ret-line-${i}`}
                >
                  <div style={{ fontSize: 12, flex: 1 }}>
                    <b>{l.item_alias}</b>
                    <br />₹{money(l.price)} × {l.qty}
                    {already ? ` · already returned ${already}` : ""}
                  </div>
                  <Input
                    type="number"
                    min="0"
                    max={remaining}
                    value={returnQtys[i] ?? "0"}
                    disabled={remaining <= 0}
                    onChange={(e) => setReturnQtys({ ...returnQtys, [i]: e.target.value })}
                    style={{ width: 80 }}
                    data-testid={`ret-qty-${i}`}
                  />
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => submitReturn(true)} data-testid="return-all">
              Return All Remaining
            </Button>
            <Button onClick={() => submitReturn(false)} data-testid="return-selected">
              Refund Selected
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "journal"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Day Sales Journal (Excel)</DialogTitle>
          </DialogHeader>
          <label className="login-label">Select date</label>
          <Input
            type="date"
            value={journalDate}
            onChange={(e) => setJournalDate(e.target.value)}
            data-testid="journal-date"
          />
          <DialogFooter>
            <Button onClick={downloadJournal} data-testid="journal-download">
              Download Journal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "zreport"} onOpenChange={(o) => !o && closeDlg()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dlg-title">Z-Report — Day Close</DialogTitle>
          </DialogHeader>
          {zreport && (
            <div style={{ fontSize: 13 }}>
              <div style={{ fontSize: 12, color: "#6b5a6d", marginBottom: 6 }}>
                Period: {zreport.period === "all" ? "All time" : "Today"}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Cash</span>
                <span>₹ {money(zreport.buckets.CASH)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Card</span>
                <span>₹ {money(zreport.buckets.CARD)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>UPI</span>
                <span>₹ {money(zreport.buckets.UPI)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Credit</span>
                <span>₹ {money(zreport.buckets.CREDIT)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Coupon / Gift</span>
                <span>₹ {money(zreport.buckets.COUPON)}</span>
              </div>
              <hr style={{ margin: "8px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Sales / Returns</span>
                <span>
                  {zreport.sale_count} / {zreport.return_count}
                </span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Total tax</span>
                <span>₹ {money(zreport.total_tax)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Refunds</span>
                <span>₹ {money(zreport.refund_total)}</span>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: "bold",
                  fontSize: 18,
                  marginTop: 6,
                }}
              >
                <span>Net total</span>
                <span>₹ {money(zreport.net_total)}</span>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => printZReport(zreport)} data-testid="zreport-print">
              Print Z-Report
            </Button>
            <Button
              onClick={() => {
                logout();
                navigate("/login");
              }}
              data-testid="zreport-logoff"
            >
              Close Day & Log Off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
